import { Button, Container, Grid, Slider, Stack } from "@mui/material";
import { UnfinishedGamesViewModel } from "./UnfinishedGamesViewModel";
import { useTranslation } from "react-i18next";
import { HeaderView } from "../../component/HeaderView";
import { LoadingView } from "../../component/LoadingView";
import { Game, TeamTurns } from "../../../domain/model/Game";
import { CustomCardView } from "../../component/CustomCardView";
import { CustomTextView } from "../../component/CustomTextView";
import {
  COLOR_BLACK,
  COLOR_LESS_BLACK,
  COLOR_LIGHT_GRAY,
  COLOR_MAIN,
  COLOR_WHITE,
} from "../../../data/colors/Colors";
import { CustomButtonView } from "../../component/CustomButtonView";
import { useNavigate } from "react-router-dom";
import { EmptyView } from "../../component/EmptyView";
import { Player } from "../../../domain/model/User";
import { providePlayerGetUnfinishedGamesUseCase } from "../../di/UseCasesModule";
import { CustomImageView } from "../../component/CustomImageView";
import {
  Category,
  convertCategoryEntityToCategory,
  convertPointsHashmapStringToMap,
} from "../../../domain/model/Category";
import useWindowDimensions from "../../../core/utils/UseWindowDimensions";
import { STRINGS_LOAD_MORE } from "../../../data/strings/Strings";
import { Difficulty } from "../../../domain/model/Question";

export function UnfnishedGamesView({ player }: { player: Player | undefined }) {
  const navigate = useNavigate();
  const viewModel = UnfinishedGamesViewModel(
    providePlayerGetUnfinishedGamesUseCase(),
  );

  const { t } = useTranslation();

  const unfinishedGames = viewModel.unfinishedGames;

  return (
    <Stack>
      <HeaderView
        title={t("unfinished_games")}
        isLayoutFullWidthInSmallerScreens={false}
      />

      {viewModel.isLoading ? (
        <LoadingView />
      ) : (
        <>
          {unfinishedGames.length == 0 ? (
            <EmptyView />
          ) : (
            <Container maxWidth={false}>
              <Grid container spacing={2} pb={3} pt={3}>
                {unfinishedGames?.map((unfinishedGame, index) => {
                  return (
                    <Grid
                      size={{ xs: 12, sm: 12, md: 12, lg: 6, xl: 6 }}
                      key={index}
                    >
                      <UnfinishedGameItemView
                        game={unfinishedGame}
                        index={index}
                        onContinueClicked={() => {
                          const gameData =
                            viewModel.convertUnfinshedGameToGameData(
                              unfinishedGame,
                            );
                          if (!gameData) return;
                          viewModel.continueGame(gameData);
                        }}
                      />
                    </Grid>
                  );
                })}
              </Grid>
              <Grid
                alignItems={"center"}
                justifyContent={"center"}
                display={"flex"}
                size={12}
                pb={3}
              >
                {viewModel.isLoadingMore ? (
                  <LoadingView minHeight="5vh" />
                ) : (
                  <>
                    {viewModel.showLoadMoreButton && (
                      <Button
                        variant="outlined"
                        sx={{
                          borderRadius: 22,
                          textTransform: "none",
                          borderColor: COLOR_LESS_BLACK,
                          "&:hover": {
                            borderColor: COLOR_LESS_BLACK,
                            background: COLOR_LIGHT_GRAY,
                          },
                        }}
                        onClick={viewModel.getUnfinishedGames}
                      >
                        <CustomTextView
                          text={STRINGS_LOAD_MORE}
                          variant="h6"
                          fontWeight="bold"
                        />
                      </Button>
                    )}
                  </>
                )}
              </Grid>
            </Container>
          )}
        </>
      )}
    </Stack>
  );
}

function UnfinishedGameItemView({
  game,
  index,
  onContinueClicked,
}: {
  game: Game;
  index: number;
  onContinueClicked: () => void;
}) {
  const { t } = useTranslation();
  const gameDetails = game.details;

  const calculateProgress = () => {
    let score = 0;

    gameDetails?.pointsHashmaps.forEach((pointHashmapItem) => {
      const pointsHashmap = convertPointsHashmapStringToMap(pointHashmapItem);

      // Loop through teamA and teamB
      pointsHashmap.forEach((teamMap) => {
        // inner teamMap is also a Map, so use forEach again
        teamMap.forEach((isCompleted) => {
          if (isCompleted) {
            score += 1;
          }
        });
      });
    });

    return score;
  };

  const sliderMaxValue =
    (gameDetails?.selectedCategories ?? []).length *
    Object.values(TeamTurns).length *
    Object.values(Difficulty).length;

  const progress = calculateProgress();

  return (
    <CustomCardView
      children={
        <Stack width={"100%"} gap={2}>
          <Stack
            gap={1}
            direction={"row"}
            alignItems={"center"}
            width={"100%"}
            justifyContent={"space-between"}
          >
            <CustomTextView
              text={`#${index + 1}`}
              variant="h5"
              fontWeight="bold"
            />
            <Stack alignItems={"center"} direction={"row"} gap={1}>
              <CustomButtonView
                text={t("continue_game")}
                onClick={onContinueClicked}
              />
            </Stack>
          </Stack>
          <Stack
            direction={"row"}
            alignItems={"center"}
            width={"100%"}
            justifyContent={"space-between"}
            gap={1}
          >
            <Stack>
              <CustomTextView
                text={t("first_team")}
                variant="body2"
                textColor={COLOR_LIGHT_GRAY}
              />
              <CustomTextView text={gameDetails?.firstTeamName} variant="h6" />
            </Stack>
            <Stack gap={1} direction={"row"} alignItems={"center"}>
              <CustomTextView
                text={gameDetails?.firstTeamScore.toString()}
                variant="h5"
                fontWeight="bold"
              />
              <CustomTextView text={"-"} variant="h3" />
              <CustomTextView
                text={gameDetails?.secondTeamScore.toString()}
                variant="h5"
                fontWeight="bold"
              />
            </Stack>
            <Stack alignItems={"end"}>
              <CustomTextView
                text={t("second_team")}
                variant="body2"
                textColor={COLOR_LIGHT_GRAY}
              />
              <CustomTextView text={gameDetails?.secondTeamName} variant="h6" />
            </Stack>
          </Stack>
          <Grid container spacing={1}>
            {gameDetails?.selectedCategories.map((c: any) => {
              if (!c._id) return <></>;
              const category = convertCategoryEntityToCategory(c);
              return (
                <Grid key={category.id} size={4}>
                  <CategoryItemView category={category} />
                </Grid>
              );
            })}
          </Grid>
          <Slider
            min={0}
            max={sliderMaxValue}
            value={progress}
            sx={{
              color: COLOR_MAIN,
              width: "100%",
              "& .MuiSlider-thumb": {
                display: "none",
              },
            }}
          />
        </Stack>
      }
    />
  );
}

export function CategoryItemView({ category }: { category: Category }) {
  const { width } = useWindowDimensions();
  const isMobile = width < 700;
  return (
    <Stack
      width={"100%"}
      alignItems={"center"}
      sx={{
        border: "solid 3px " + COLOR_MAIN,
        borderRadius: 4,
        boxShadow: 2,
        background: COLOR_MAIN,
      }}
    >
      <CustomImageView
        src={category.imageUrl}
        width={"100%"}
        height={isMobile ? 120 : 160}
        scale="contain"
        props={{
          borderRadius: 3.5,
          background: "white",
        }}
      />
      <Stack
        sx={{
          background: COLOR_MAIN,
          width: "100%",
          alignItems: "center",
          borderRadius: 3.5,
          p: 0.2,
        }}
      >
        <CustomTextView
          text={category.title}
          textColor={COLOR_WHITE}
          fontWeight="bold"
          variant={isMobile ? "body2" : "h6"}
          textAlign="center"
        />
      </Stack>
    </Stack>
  );
}
